import React from 'react';
import { isNil } from 'ramda';
import styled, { css } from 'styled-components';
import Link, { Props as LinkProps } from '~/components/molecule/Link';

export type Props = LinkProps & {
  dataTestId?: string;
  isSelected?: boolean;
  isDeleted?: boolean;
  isDisabled?: boolean;
};

const Row = React.forwardRef<HTMLAnchorElement, Props>(
  (
    {
      dataTestId,
      children,
      isSelected = false,
      isDeleted = false,
      isDisabled = false,
      ...rest
    },
    ref,
  ) => {
    const isLink = !isNil(rest.to);

    return (
      <StyledLink
        {...rest}
        ref={ref}
        dataTestId={dataTestId}
        $isSelected={isSelected}
        $isDeleted={isDeleted}
        $isDisabled={isDisabled}
        $isLink={isLink}
        role={isLink ? 'link' : 'row'}
        aria-selected={isSelected}
        aria-disabled={isDisabled}
      >
        {children}
      </StyledLink>
    );
  },
);

type StyledTrProps = {
  $isSelected: boolean;
  $isDeleted: boolean;
  $isDisabled: boolean;
  $isLink: boolean;
};

const StyledLink = styled(Link)<StyledTrProps>(
  ({ theme, $isSelected, $isDeleted, $isLink, $isDisabled }) => css`
    display: flex;
    cursor: ${$isLink ? 'pointer' : 'default'};
    padding: ${theme.space('s')} ${theme.space('m')};
    background-color: ${$isSelected
      ? theme.color('white', 'dark')
      : theme.color('white')};
    font-weight: ${$isSelected
      ? theme.fontWeight('medium')
      : theme.fontWeight('regular')};

    border-top: 1px solid ${theme.color('tertiary')};
    height: ${$isDeleted ? 0 : 'auto'};
    overflow-y: ${$isDeleted ? 'hidden' : 'unset'};

    width: 100%;
    text-decoration: none;
    color: inherit;

    /* Focus styles for accessibility */
    &:focus-visible {
      outline: 2px solid ${theme.color('primary')};
      outline-offset: -2px;
    }

    &:hover {
      background-color: ${theme.color('tertiary', 'light')};
    }

    &:first-of-type {
      border-top: none;
    }

    &:last-of-type {
      border-bottom-left-radius: ${theme.getTokens().border.radius.m};
      border-bottom-right-radius: ${theme.getTokens().border.radius.m};
    }

    ${$isDisabled &&
    css`
      pointer-events: none;
      background-color: ${theme.color('tertiary', 'light')};
      opacity: 0.5;
    `}
  `,
);

export default Row;
